/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  keepPreviousData,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';

import {
  type CreateSessionPayload,
  type Page,
  type RateSessionInput,
  type SaveNotePayload,
  type Session,
  type SessionNote,
  type SessionsPageParams,
  sessionsApi,
} from '../api/sessions.js';

const SESSIONS_KEY = ['sessions'] as const;
const noteKey = (id: string): readonly unknown[] => ['sessions', id, 'note'];

export function useSessions(): UseQueryResult<readonly Session[]> {
  return useQuery({ queryKey: SESSIONS_KEY, queryFn: () => sessionsApi.list() });
}

/** Paginated, searchable session list for the role-scoped management table (P1-1). */
export function useSessionsPage(params: SessionsPageParams): UseQueryResult<Page<Session>> {
  return useQuery({
    queryKey: ['sessions', 'page', params],
    queryFn: () => sessionsApi.listPage(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateSession(): UseMutationResult<Session, Error, CreateSessionPayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSessionPayload) => sessionsApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

export function useSession(id: string): UseQueryResult<Session> {
  return useQuery({ queryKey: ['sessions', id], queryFn: () => sessionsApi.getOne(id) });
}

export function useSessionNote(id: string): UseQueryResult<SessionNote | null> {
  return useQuery({ queryKey: noteKey(id), queryFn: () => sessionsApi.getNote(id) });
}

interface TransitionVars {
  readonly id: string;
  readonly action: 'start' | 'complete' | 'no-show';
}

export function useTransitionSession(): UseMutationResult<Session, Error, TransitionVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: TransitionVars) => sessionsApi.transition(vars.id, vars.action),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

interface CancelVars {
  readonly id: string;
  readonly reason?: string | undefined;
}

export function useCancelSession(): UseMutationResult<Session, Error, CancelVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: CancelVars) => sessionsApi.cancel(vars.id, vars.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

export function useClientLate(): UseMutationResult<Session, Error, CancelVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: CancelVars) => sessionsApi.clientLate(vars.id, vars.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

export function useConsultantCheckIn(): UseMutationResult<Session, Error, { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string }) => sessionsApi.consultantCheckIn(vars.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

export function useConsultantAbsent(): UseMutationResult<
  Session,
  Error,
  { id: string; reason: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      sessionsApi.consultantAbsent(vars.id, vars.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

interface RateVars {
  readonly id: string;
  readonly input: RateSessionInput;
}

export function useRateSession(): UseMutationResult<Session, Error, RateVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: RateVars) => sessionsApi.rate(vars.id, vars.input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

interface RescheduleVars {
  readonly id: string;
  /** ISO-8601 instant. */
  readonly scheduledStart: string;
}

export function useRescheduleSession(): UseMutationResult<Session, Error, RescheduleVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: RescheduleVars) => sessionsApi.reschedule(vars.id, vars.scheduledStart),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

export function useSaveNote(id: string): UseMutationResult<SessionNote, Error, SaveNotePayload> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SaveNotePayload) => sessionsApi.saveNote(id, payload),
    onSuccess: (note) => {
      qc.setQueryData(noteKey(id), note);
    },
  });
}

export function useSignNote(id: string): UseMutationResult<SessionNote, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => sessionsApi.signNote(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: noteKey(id) });
      void qc.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}
